/**
 * `numbersApi` — RTK Query surface for the phone-number registry (P4-T6,
 * design §15). Mirrors the per-feature `injectEndpoints` convention of the
 * P1/P2 slices (`agentsApi`, `projectsApi`, `reportsApi`): shared `apiSlice`,
 * relative urls, `{ data: … }` envelope unwrapping, tag invalidation.
 *
 * Url convention: `v1/voice-bridge/…` mirrors the mounted controller path
 * (`numbers.controller.ts:50`) verbatim — the same posture reportsApi takes
 * for `v1/reports/…`. The dev vite proxy currently lacks the matching
 * `/api/v1/voice-bridge` special case (the organizations-directory entry
 * pattern, `vite.config.ts:72-76`); that one-line config change is shell-
 * owned, so it was PROPOSED to the orchestrator, not applied here.
 *
 * `voice/voices` rides the existing `/api/voice` proxy (voice-service :5000).
 * Org factories are NOT re-declared here — the bind dialogs reuse the P2
 * `agentsApi.getFactories({ orgId })` query (design §15 H1), and org projects
 * reuse the P1 `orgApi.getOrgProjects` query.
 */

import { apiSlice } from '@/store/apiSlice';
import type { DataEnvelope } from '@/features/orgs/types';

import type {
  BindNumberArgs,
  CreatePhoneNumberDto,
  ListNumbersArgs,
  NumberListResponse,
  PhoneNumberView,
  UpdateNumberArgs,
  VoiceView,
} from '../types';

function unwrap<T>(raw: DataEnvelope<T> | T | undefined): T | undefined {
  if (raw && typeof raw === 'object' && 'data' in (raw as DataEnvelope<T>)) {
    return (raw as DataEnvelope<T>).data;
  }
  return raw as T | undefined;
}

const numbersTaggedApi = apiSlice.enhanceEndpoints({
  addTagTypes: ['NumberList', 'VoiceList'],
});

export const numbersApi = numbersTaggedApi.injectEndpoints({
  endpoints: (builder) => ({
    /** `GET /v1/voice-bridge/numbers?page&limit&projectId&isActive` (§6.4). */
    listNumbers: builder.query<NumberListResponse, ListNumbersArgs>({
      query: (args) => ({
        url: 'v1/voice-bridge/numbers',
        params: {
          page: args.page,
          limit: args.limit,
          projectId: args.projectId,
          isActive: args.isActive,
        },
      }),
      // The list envelope carries `pagination` beside `data`, so it is read
      // structurally instead of unwrapped (unwrap would drop the pagination).
      transformResponse: (raw: DataEnvelope<NumberListResponse> | NumberListResponse) => {
        const body = (raw ?? {}) as Partial<NumberListResponse>;
        const items = Array.isArray(body.data) ? body.data : [];
        return {
          data: items,
          pagination: body.pagination ?? {
            page: 1,
            limit: items.length,
            total: items.length,
          },
        };
      },
      providesTags: [{ type: 'NumberList', id: 'LIST' }],
    }),

    /** `POST /v1/voice-bridge/numbers` (§7). Duplicate → 409, surfaced inline. */
    createNumber: builder.mutation<PhoneNumberView, CreatePhoneNumberDto>({
      query: (body) => ({ url: 'v1/voice-bridge/numbers', method: 'POST', body }),
      transformResponse: (raw: DataEnvelope<PhoneNumberView>) =>
        unwrap<PhoneNumberView>(raw) as PhoneNumberView,
      invalidatesTags: [{ type: 'NumberList', id: 'LIST' }],
    }),

    /** `PATCH /v1/voice-bridge/numbers/:id` (§8/§10). */
    updateNumber: builder.mutation<PhoneNumberView, UpdateNumberArgs>({
      query: ({ id, changes }) => ({
        url: `v1/voice-bridge/numbers/${id}`,
        method: 'PATCH',
        body: changes,
      }),
      transformResponse: (raw: DataEnvelope<PhoneNumberView>) =>
        unwrap<PhoneNumberView>(raw) as PhoneNumberView,
      invalidatesTags: [{ type: 'NumberList', id: 'LIST' }],
    }),

    /** `DELETE /v1/voice-bridge/numbers/:id` (§11). Allowed while bound. */
    deleteNumber: builder.mutation<unknown, string>({
      query: (id) => ({ url: `v1/voice-bridge/numbers/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'NumberList', id: 'LIST' }],
    }),

    /** `POST /v1/voice-bridge/numbers/:id/bind` (§9). Overwrites existing binding. */
    bindNumber: builder.mutation<PhoneNumberView, BindNumberArgs>({
      query: ({ id, factoryId }) => ({
        url: `v1/voice-bridge/numbers/${id}/bind`,
        method: 'POST',
        body: { factoryId },
      }),
      transformResponse: (raw: DataEnvelope<PhoneNumberView>) =>
        unwrap<PhoneNumberView>(raw) as PhoneNumberView,
      invalidatesTags: [{ type: 'NumberList', id: 'LIST' }],
    }),

    /** `GET /voice/voices` — owner-scoped voices for the forwarding step (§12.1). */
    listVoices: builder.query<VoiceView[], void>({
      query: () => ({ url: 'voice/voices' }),
      transformResponse: (raw: DataEnvelope<VoiceView[]> | VoiceView[]) =>
        unwrap<VoiceView[]>(raw) ?? [],
      providesTags: [{ type: 'VoiceList', id: 'LIST' }],
    }),
  }),
});

export const {
  useListNumbersQuery,
  useCreateNumberMutation,
  useUpdateNumberMutation,
  useDeleteNumberMutation,
  useBindNumberMutation,
  useListVoicesQuery,
} = numbersApi;
